import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { rewriteDeal, searchAccounts, getRewriteContext } from '@/services/deals.service.js';
import { resolveAccount } from '@/services/account.service.js';
import { POLICYHOLDER_TYPES } from './EditNamedInsuredSheet.jsx';
import { formatDate, daysUntil } from '@/lib/date-display.js';

// New deal from this policy (REWRITE-AND-CREDITS-SPEC Part A, generalized by
// LAPSED-CLIENT-REUP-SPEC §3). Clones the source deal's risk into a NEW
// new-business deal: under a different insured (entity change, ownership
// transfer) or the SAME insured (a client whose policy ended and wants coverage
// again). Never a renewal: the backend never sets Prior_Policy. Nothing emails
// anyone. With the session switch on, the chain also creates the client session
// with invites suppressed, so Manage pilots writes to a real PHF row and the
// pilot's link can be sent on its own.
//
// Everything the sheet shows about the SOURCE comes from one server-normalized
// payload (GET rewrite-context), never from `item`: two different shapes arrive
// under that prop name depending on which surface opened the door (§3.0).
//
// Entity-type options exclude the "Not set" row (a new entity always names a
// policyholder type).
const ENTITY_TYPES = POLICYHOLDER_TYPES.filter((t) => t.value !== '');

const fieldClass = cn(
  'flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
  'placeholder:text-text-dim',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The insured default follows the source deal's lifecycle state: a policy that
// ended (expired, cancelled) is most often re-upped by the same insured; a live
// one is most often re-placed under a new entity (the Ngo case).
export function defaultInsuredMode(lifecycleState) {
  return lifecycleState === 'expired' || lifecycleState === 'cancelled' ? 'same' : 'new';
}

function fmtMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

// "Ended Jan 17, 2026, 8 months ago" for a term end in the past; null otherwise.
function endedLine(termEnd) {
  if (!termEnd) return null;
  const days = daysUntil(termEnd);
  if (!Number.isFinite(days) || days >= 0) return null;
  const months = Math.round(-days / 30);
  const ago = months >= 1 ? `${months} month${months === 1 ? '' : 's'} ago` : `${-days} day${days === -1 ? '' : 's'} ago`;
  return `Ended ${formatDate(termEnd)}, ${ago}`;
}

export default function RewriteDealSheet({ dealId, item, lifecycleState, open, onOpenChange }) {
  const navigate = useNavigate();
  const sourceAccountId = item?.account_id || item?.crm_account_id || item?.Account_Name?.id || null;

  const [insured, setInsured] = useState('new'); // 'same' | 'existing' | 'new'
  const [effectiveDate, setEffectiveDate] = useState(todayYMD());
  const [submitting, setSubmitting] = useState(false);

  // Source context (server-normalized). null while loading; contextError when
  // the read failed.
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(null);

  // Pilot + session switches.
  const [namePrimary, setNamePrimary] = useState(true);
  const [initiateSession, setInitiateSession] = useState(false);

  // New-entity fields.
  const [entityName, setEntityName] = useState('');
  const [entityType, setEntityType] = useState('Entity'); // LLC, the common rewrite case
  const [addr, setAddr] = useState({ street: '', city: '', state: '', zip: '' });

  // Existing-account search.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);

  // Reset on every open. The insured default lives HERE, not only in useState:
  // a default set once is correct once and wrong on every reopen.
  useEffect(() => {
    if (!open) return;
    const mode = defaultInsuredMode(lifecycleState);
    setInsured(mode);
    setInitiateSession(mode === 'same');
    setNamePrimary(true);
    setEffectiveDate(todayYMD());
    setEntityName('');
    setEntityType('Entity');
    setAddr({ street: '', city: '', state: '', zip: '' });
    setQuery('');
    setResults([]);
    setPicked(null);
    setContext(null);
    setContextError(null);

    let alive = true;
    getRewriteContext(dealId)
      .then((r) => { if (alive) setContext(r?.context || null); })
      .catch((err) => { if (alive) setContextError(err?.message || 'could not load'); });

    // Prefill the new-entity address from the source account (new LLCs usually
    // share the person's address).
    if (sourceAccountId) {
      resolveAccount(sourceAccountId)
        .then((acct) => {
          if (!alive || !acct) return;
          setAddr({
            street: acct.account_street || '',
            city: acct.account_city || '',
            state: acct.account_st || '',
            zip: acct.account_zip || '',
          });
        })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [open, dealId, sourceAccountId, lifecycleState]);

  // Debounced account search (existing mode).
  useEffect(() => {
    if (insured !== 'existing') return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchAccounts(q)
        .then((r) => { if (alive) setResults(r?.results || []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [query, insured]);

  function pickInsured(mode) {
    setInsured(mode);
    // The session default follows the mode: on for a re-up, off when the
    // insured changes (the prior ruling: a rewrite creates the deal only).
    setInitiateSession(mode === 'same');
  }

  const aircraft = context?.aircraft || [];
  const pilots = context?.pilots || [];
  const contactName = context?.contact?.name || item?.contact_name || item?.Contact_Name?.name || null;
  const contactResolvable = !!context?.contact?.id;
  // The checkbox is offered ONLY when the source roster is empty (LAPSED-CLIENT-
  // REUP-SPEC §1.3, Erik's case). Offered beside a roster that already names
  // pilots, and defaulted on, it put Steve Distante on every aircraft of the
  // Wings Up I&R, whose roster is its chief instructor (David, 2026-10-24).
  // While the context is loading or failed it is offered with the skip note;
  // the backend refuses it beside a non-empty roster either way.
  const contactOnRoster = context?.contact?.on_roster === true;
  // A property policy (hangar, builders risk) carries no aircraft and no
  // pilots, and its client session starts from the hangar intake, not the
  // collect-mode initiate. The line comes from the server, never from item.
  const isProperty = context?.is_property === true;
  const showPilotCheckbox = !contactOnRoster && !isProperty && (!context || pilots.length === 0);
  const ended = endedLine(context?.term_end);

  const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
  const canSubmit =
    YMD_RE.test(effectiveDate) &&
    (insured === 'same' ? true : insured === 'new' ? entityName.trim().length > 0 : !!picked) &&
    !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const target_account = insured === 'same'
        ? undefined
        : insured === 'existing'
          ? { account_id: picked.id, account_name: picked.name }
          : {
              create: {
                name: entityName.trim(),
                entity_type: entityType,
                address: { street: addr.street, city: addr.city, state: addr.state, zip: addr.zip },
              },
            };
      const res = await rewriteDeal(dealId, {
        insured,
        ...(target_account ? { target_account } : {}),
        effective_date: effectiveDate,
        name_primary_as_pilot: showPilotCheckbox && namePrimary && contactResolvable,
        initiate_session: initiateSession && !isProperty,
      });
      toast.success(`Created ${res.deal_name}${res.session_token ? ', client session ready (no email sent)' : ''}`);
      // Every warning the chain returned, on the same surface as the success.
      for (const w of (res.warnings || [])) toast.info(w);
      onOpenChange?.(false);
      if (res.new_account_id && res.new_deal_id) {
        navigate(`/account/${res.new_account_id}/deal/${res.new_deal_id}`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body?.error || err.code) : (err.message || 'failed');
      toast.error(`Couldn't create the new deal: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  const insuredButton = (mode, label) => (
    <Button type="button" variant={insured === mode ? 'default' : 'ghost'} size="sm" onClick={() => pickInsured(mode)}>
      {label}
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto md:max-w-lg">
        <SheetHeader>
          <SheetTitle>New deal from this policy</SheetTitle>
          <SheetDescription>
            {isProperty
              ? 'Starts a new deal for this hangar policy, for the same insured (a policy that ended) or a different one (entity change, ownership transfer).'
              : 'Starts a new deal from this policy\'s aircraft and pilots, for the same insured (a policy that ended) or a different one (entity change, ownership transfer).'}
            {' '}This is new business, not a renewal. It does not cancel anything and does not email anyone.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Source summary, from the server-normalized context */}
          <div className="rounded-card border border-border bg-bg/40 px-3 py-2 text-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-text-dim">From this policy</div>
            {contextError ? (
              <div className="mt-0.5 text-xs text-warning">Could not load the source policy ({contextError}). You can still create the deal.</div>
            ) : !context ? (
              <div className="mt-0.5 text-xs text-text-dim">Loading the source policy…</div>
            ) : (
              <>
                <div className="mt-0.5 font-medium text-text">{context.account?.name || context.deal_name || 'this policy'}</div>
                <div className="mt-0.5 text-xs text-text-muted">
                  {[context.carrier_name, fmtMoney(context.premium), context.policy_number].filter(Boolean).join(' · ') || 'carrier and premium not on the source'}
                </div>
                {ended ? (
                  <div className="mt-0.5 text-xs text-warning">{ended}</div>
                ) : context.term_end ? (
                  <div className="mt-0.5 text-xs text-text-dim">Term ends {formatDate(context.term_end)}</div>
                ) : (
                  <div className="mt-0.5 text-xs text-text-dim">Term end not on the source</div>
                )}
                <div className="mt-0.5 text-xs text-text-dim">
                  {isProperty
                    ? 'Hangar policy: no aircraft or pilots carry over. Enter the location and terms on the new deal.'
                    : `${aircraft.length} aircraft · ${pilots.length} named pilot${pilots.length === 1 ? '' : 's'} carry over`}
                </div>
                {/* What creating this deal will close on the source (§3.1). The
                    clear runs after the response, so this line is the only place
                    it can be surfaced. */}
                {context.will_close === null ? (
                  <div className="mt-1 text-xs text-warning">
                    Could not check which follow-ups on this policy will close{context.will_close_error ? ` (${context.will_close_error})` : ''}.
                  </div>
                ) : context.will_close.length === 0 ? (
                  <div className="mt-1 text-xs text-text-dim">No open follow-ups on this policy will close.</div>
                ) : (
                  <div className="mt-1 text-xs text-text-muted">
                    Creating this deal will close on this policy: {context.will_close.map((f) => f.label).join(', ')}.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Insured */}
          <div>
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Insured on the new deal</span>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {insuredButton('same', `Same insured${context?.account?.name ? ` (${context.account.name})` : ''}`)}
              {insuredButton('existing', 'Existing account')}
              {insuredButton('new', 'New entity')}
            </div>
          </div>

          {insured === 'new' && (
            <div className="space-y-2">
              <input autoFocus type="text" value={entityName}
                onChange={(e) => setEntityName(e.target.value.slice(0, 200))}
                placeholder="New insured name (e.g. The Flying Machine LLC)" className={fieldClass} />
              <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className={fieldClass}>
                {ENTITY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input type="text" value={addr.street}
                onChange={(e) => setAddr((a) => ({ ...a, street: e.target.value.slice(0, 120) }))}
                placeholder="Street (prefilled from source)" className={fieldClass} />
              <div className="flex gap-2">
                <input type="text" value={addr.city}
                  onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value.slice(0, 80) }))}
                  placeholder="City" className={cn(fieldClass, 'flex-1')} />
                <input type="text" value={addr.state}
                  onChange={(e) => setAddr((a) => ({ ...a, state: e.target.value.slice(0, 20) }))}
                  placeholder="ST" className={cn(fieldClass, 'w-16')} />
                <input type="text" value={addr.zip}
                  onChange={(e) => setAddr((a) => ({ ...a, zip: e.target.value.slice(0, 12) }))}
                  placeholder="ZIP" className={cn(fieldClass, 'w-24')} />
              </div>
            </div>
          )}

          {insured === 'existing' && (
            <div className="space-y-2">
              <input autoFocus type="text" value={query}
                onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
                placeholder="Search existing accounts…" className={fieldClass} />
              {picked ? (
                <div className="rounded-card border border-accent bg-accent/10 px-3 py-2 text-sm">
                  <span className="font-medium text-text">{picked.name}</span>
                  <button type="button" className="ml-2 text-xs text-text-dim underline"
                    onClick={() => setPicked(null)}>change</button>
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto rounded-card border border-border">
                  {searching && <div className="px-3 py-2 text-xs text-text-dim">Searching…</div>}
                  {!searching && query.trim().length >= 2 && results.length === 0 &&
                    <div className="px-3 py-2 text-xs text-text-dim">No accounts found.</div>}
                  {results.map((r) => (
                    <button key={r.id} type="button" onClick={() => setPicked(r)}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-bg/60">
                      <span className="text-text">{r.name}</span>
                      {r.entity_type && <span className="ml-2 text-xs text-text-dim">{r.entity_type}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Effective date */}
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Effective date</span>
            <input type="date" value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)} className={cn(fieldClass, 'mt-1.5')} />
          </label>

          {/* Primary contact carries from the source deal. */}
          <div className="rounded-card border border-border bg-bg/40 px-3 py-2 text-xs text-text-muted">
            Primary contact carries from the source deal{contactName ? ` (${contactName})` : ''}.
            Change it on the new deal after creating if the human is different.
          </div>

          {/* Pilots (not a question on a property policy) */}
          {!isProperty && (
          <div className="rounded-card border border-border bg-bg/40 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-text-dim">Who may fly</div>
            {pilots.length > 0 && (
              <div className="mt-1 text-xs text-text-muted">
                Carrying over: {pilots.map((p) => p.name).filter(Boolean).join(', ') || `${pilots.length} pilot${pilots.length === 1 ? '' : 's'}`}
              </div>
            )}
            {context && pilots.length === 0 && (
              <div className="mt-1 text-xs text-text-dim">No named pilots on the source policy.</div>
            )}
            {showPilotCheckbox && (
              <label className={cn('mt-1.5 flex items-start gap-2 text-xs', contactResolvable || !context ? 'text-text' : 'text-text-dim')}>
                <input type="checkbox" className="mt-0.5" checked={namePrimary && (contactResolvable || !context)}
                  disabled={!!context && !contactResolvable}
                  onChange={(e) => setNamePrimary(e.target.checked)} />
                <span>
                  Name {contactName || 'the primary contact'} as a pilot on every aircraft
                  {!context && ' (skipped if already on the roster)'}
                  {context && !contactResolvable && ' (no primary contact on the source deal, so nobody to name)'}
                </span>
              </label>
            )}
            {aircraft.length > 0 && (
              <div className="mt-1 text-[10px] text-text-dim">
                {aircraft.map((a) => a.tail_number).filter(Boolean).join(', ')}. Edit the roster on the new deal after creating.
              </div>
            )}
          </div>
          )}

          {/* Session switch. A hangar policy's client session starts from the
              hangar intake, so the collect-mode switch is not offered. */}
          {isProperty && (
            <div className="rounded-card border border-border bg-bg/40 px-3 py-2 text-xs text-text-muted">
              No client session is created here: a hangar policy&apos;s session starts from the hangar intake.
            </div>
          )}
          {!isProperty && (
          <label className="flex items-start gap-2 rounded-card border border-border bg-bg/40 px-3 py-2 text-xs text-text">
            <input type="checkbox" className="mt-0.5" checked={initiateSession}
              onChange={(e) => setInitiateSession(e.target.checked)} />
            <span>
              Create the client session now (no email is sent).
              <span className="block text-text-dim">Manage pilots then writes the pilot history to a real form, and the pilot&apos;s PHF link can be sent on its own.</span>
            </span>
          </label>
          )}

          <SheetFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? 'Creating…' : 'Create new deal'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
